<template>
  <div class="sig-pad" :class="{ empty: isEmpty }">
    <canvas ref="canvas" :style="{ height: height + 'px' }" aria-label="لوحة التوقيع"></canvas>
    <span class="sig-hint" v-if="isEmpty">وقّع هنا بإصبعك أو بالماوس</span>
    <span class="sig-line"></span>
    <button type="button" class="sig-clear" v-if="!isEmpty" @click="clear()">
      <i class="fa fa-eraser"></i> مسح
    </button>
  </div>
</template>

<script>
// Signature canvas for touch and mouse (pointer events), sharp on high-DPI screens.
// toDataURL() returns a PNG cropped to the drawing, or "" when nothing was drawn.
export default {
  props: {
    height: { type: Number, default: 180 },
    color: { type: String, default: '#0F1733' },
  },
  data() {
    return { isEmpty: true, drawing: false, last: null, ratio: 1 };
  },
  mounted() {
    var c = this.$refs.canvas;
    this.resize();
    c.addEventListener('pointerdown', this.down);
    c.addEventListener('pointermove', this.move);
    window.addEventListener('pointerup', this.up);
    window.addEventListener('resize', this.resize);
  },
  beforeDestroy() {
    window.removeEventListener('pointerup', this.up);
    window.removeEventListener('resize', this.resize);
  },
  methods: {
    resize() {
      var c = this.$refs.canvas;
      if(!c || !this.isEmpty) return; // keep a drawn signature as it is
      this.ratio = Math.max(window.devicePixelRatio || 1, 1);
      c.width = c.offsetWidth * this.ratio;
      c.height = this.height * this.ratio;
      var ctx = c.getContext('2d');
      ctx.scale(this.ratio, this.ratio);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = this.color;
      ctx.lineWidth = 2.6;
    },
    point(e) {
      var r = this.$refs.canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    },
    down(e) {
      e.preventDefault();
      this.$refs.canvas.setPointerCapture?.(e.pointerId);
      this.drawing = true;
      this.last = this.point(e);
      var ctx = this.$refs.canvas.getContext('2d');
      ctx.beginPath();
      ctx.arc(this.last.x, this.last.y, 1.2, 0, Math.PI * 2);
      ctx.fillStyle = this.color;
      ctx.fill();
      this.isEmpty = false;
      this.$emit('change', false);
    },
    move(e) {
      if(!this.drawing) return;
      e.preventDefault();
      var p = this.point(e), ctx = this.$refs.canvas.getContext('2d');
      var mid = { x: (this.last.x + p.x) / 2, y: (this.last.y + p.y) / 2 };
      ctx.beginPath();
      ctx.moveTo(this.last.mid ? this.last.mid.x : this.last.x, this.last.mid ? this.last.mid.y : this.last.y);
      ctx.quadraticCurveTo(this.last.x, this.last.y, mid.x, mid.y);
      ctx.stroke();
      this.last = Object.assign(p, { mid: mid });
    },
    up() {
      this.drawing = false;
      this.last = null;
    },
    clear() {
      var c = this.$refs.canvas;
      c.getContext('2d').clearRect(0, 0, c.width, c.height);
      this.isEmpty = true;
      this.$emit('change', true);
    },
    toDataURL() {
      if(this.isEmpty) return '';
      var c = this.$refs.canvas, ctx = c.getContext('2d');
      var data = ctx.getImageData(0, 0, c.width, c.height).data;
      var minX = c.width, minY = c.height, maxX = -1, maxY = -1;
      for(var y = 0; y < c.height; y++){
        for(var x = 0; x < c.width; x++){
          if(data[(y * c.width + x) * 4 + 3] > 0){
            if(x < minX) minX = x;
            if(x > maxX) maxX = x;
            if(y < minY) minY = y;
            if(y > maxY) maxY = y;
          }
        }
      }
      if(maxX < 0) return '';
      var pad = 8 * this.ratio;
      minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
      maxX = Math.min(c.width - 1, maxX + pad); maxY = Math.min(c.height - 1, maxY + pad);
      var out = document.createElement('canvas');
      out.width = maxX - minX + 1;
      out.height = maxY - minY + 1;
      out.getContext('2d').drawImage(c, minX, minY, out.width, out.height, 0, 0, out.width, out.height);
      return out.toDataURL('image/png');
    },
  },
};
</script>

<style scoped>
.sig-pad {
  position: relative;
  border: 1.5px dashed var(--sk-field, #BFC6D1);
  border-radius: 14px;
  background: #fff;
  touch-action: none;
  overflow: hidden;
}
.sig-pad:not(.empty) {
  border-style: solid;
  border-color: var(--sk-p, #1D4ED8);
}
.sig-pad canvas {
  display: block;
  width: 100%;
  cursor: crosshair;
  touch-action: none;
}
.sig-hint {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--sk-faint, #98A2B3);
  pointer-events: none;
  font-size: 14px;
}
.sig-line {
  position: absolute;
  left: 18px;
  right: 18px;
  bottom: 34px;
  border-bottom: 1px solid var(--sk-line2, #E6E9EF);
  pointer-events: none;
}
.sig-clear {
  position: absolute;
  top: 8px;
  left: 8px;
  border: 1px solid var(--sk-line, #D5DAE3);
  background: #fff;
  color: var(--sk-text, #344054);
  border-radius: 8px;
  padding: 3px 10px;
  font-size: 12px;
}
</style>
